/**
 * OnboardingOverlay Component
 * 
 * A guided tour overlay that highlights UI elements and shows step-by-step instructions.
 * Uses a spotlight effect to draw attention to target elements.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate, useLocation } from 'react-router-dom'
import { useOnboardingContext } from '../../context/OnboardingContext'
import { Icon } from '../common/Icon'
import { Button } from '../common/Button'
import './OnboardingOverlay.css'

// Animation variants
const overlayVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
}

const tooltipVariants = {
  initial: { 
    opacity: 0, 
    scale: 0.9,
    y: 10,
  },
  animate: { 
    opacity: 1, 
    scale: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
    }
  },
  exit: { 
    opacity: 0, 
    scale: 0.95,
    transition: {
      duration: 0.2,
    }
  },
}

/**
 * Calculate tooltip position relative to target element
 */
function calculateTooltipPosition(targetRect, tooltipRect, position, padding = 16) {
  if (!targetRect) {
    // Center position for no target
    return {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    }
  }

  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const tooltipWidth = tooltipRect?.width || 360
  const tooltipHeight = tooltipRect?.height || 200

  let top, left

  switch (position) {
    case 'bottom':
      top = targetRect.bottom + padding
      left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2)
      break
    case 'top':
      top = targetRect.top - tooltipHeight - padding
      left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2)
      break
    case 'left':
      top = targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2)
      left = targetRect.left - tooltipWidth - padding
      break
    case 'right':
      top = targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2)
      left = targetRect.right + padding
      break
    default:
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }
  }

  // Keep tooltip within viewport bounds
  if (left < padding) left = padding
  if (left + tooltipWidth > viewportWidth - padding) {
    left = viewportWidth - tooltipWidth - padding
  }
  if (top < padding) top = padding
  if (top + tooltipHeight > viewportHeight - padding) {
    top = viewportHeight - tooltipHeight - padding
  }

  return {
    top: `${top}px`,
    left: `${left}px`,
    transform: 'none',
  }
}

/**
 * OnboardingOverlay Component
 */
export function OnboardingOverlay() {
  const navigate = useNavigate()
  const location = useLocation()
  const {
    shouldShow,
    currentStep,
    currentStepIndex,
    totalSteps,
    progress,
    handleCompleteStep,
    skipStep,
    dismissOnboarding,
    getEffectiveStep,
  } = useOnboardingContext()

  const [targetRect, setTargetRect] = useState(null)
  const [tooltipStyle, setTooltipStyle] = useState({})
  const tooltipRef = useRef(null)

  // Get the effective step (accounting for auto-skipped steps)
  const effectiveStep = getEffectiveStep()

  // Find and track target element
  useEffect(() => {
    if (!effectiveStep?.target) {
      setTargetRect(null)
      setTooltipStyle(calculateTooltipPosition(null, null, 'center'))
      return
    }

    const updateTargetRect = () => {
      const element = document.querySelector(effectiveStep.target)
      if (element) {
        const rect = element.getBoundingClientRect()
        setTargetRect(rect)
        
        const tooltipRect = tooltipRef.current?.getBoundingClientRect()
        setTooltipStyle(calculateTooltipPosition(rect, tooltipRect, effectiveStep.position))
      } else {
        setTargetRect(null)
        setTooltipStyle(calculateTooltipPosition(null, null, 'center'))
      }
    }

    // Initial update
    updateTargetRect()

    // Update on resize/scroll
    window.addEventListener('resize', updateTargetRect)
    window.addEventListener('scroll', updateTargetRect, true)

    // Also update periodically in case of dynamic content
    const interval = setInterval(updateTargetRect, 500)

    return () => {
      window.removeEventListener('resize', updateTargetRect)
      window.removeEventListener('scroll', updateTargetRect, true)
      clearInterval(interval)
    }
  }, [effectiveStep])

  // Handle action button click
  const handleAction = useCallback(async () => {
    if (!effectiveStep) return

    switch (effectiveStep.action) {
      case 'next':
      case 'finish':
        await handleCompleteStep()
        break
      case 'create':
        // Just mark complete - the user action will trigger the next step
        await handleCompleteStep()
        break
      default:
        await handleCompleteStep()
    }
  }, [effectiveStep, handleCompleteStep])

  // Handle skip
  const handleSkip = useCallback(async () => {
    await skipStep()
  }, [skipStep])

  // Handle dismiss
  const handleDismiss = useCallback(async () => {
    await dismissOnboarding()
  }, [dismissOnboarding])

  if (!shouldShow || !effectiveStep) return null

  const isCenter = effectiveStep.position === 'center' || !targetRect
  const actionLabel = effectiveStep.action === 'finish' 
    ? 'Get Started' 
    : effectiveStep.action === 'create'
      ? 'Got it'
      : 'Next'

  return (
    <AnimatePresence>
      <motion.div
        className="onboarding-overlay"
        variants={overlayVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        {/* Backdrop with spotlight cutout */}
        <div className="onboarding-overlay__backdrop">
          {targetRect && (
            <div
              className="onboarding-overlay__spotlight"
              style={{
                top: targetRect.top - 8,
                left: targetRect.left - 8,
                width: targetRect.width + 16,
                height: targetRect.height + 16,
              }}
            />
          )}
        </div>

        {/* Tooltip Card */}
        <motion.div
          ref={tooltipRef}
          className={`onboarding-overlay__tooltip ${isCenter ? 'onboarding-overlay__tooltip--center' : ''}`}
          style={tooltipStyle}
          variants={tooltipVariants}
          key={effectiveStep.id}
        >
          {/* Glow effect */}
          <div className="onboarding-overlay__glow" />

          {/* Close button */}
          <button
            className="onboarding-overlay__close"
            onClick={handleDismiss}
            aria-label="Close onboarding"
          >
            <Icon name="close" size={16} />
          </button>

          {/* Progress bar */}
          <div className="onboarding-overlay__progress">
            <div 
              className="onboarding-overlay__progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Step indicator */}
          <div className="onboarding-overlay__step-indicator">
            Step {currentStepIndex + 1} of {totalSteps}
          </div>

          {/* Content */}
          <div className="onboarding-overlay__content">
            <h3 className="onboarding-overlay__title">
              {effectiveStep.title}
            </h3>
            <p className="onboarding-overlay__description">
              {effectiveStep.description}
            </p>
          </div>

          {/* Actions */}
          <div className="onboarding-overlay__actions">
            {currentStepIndex > 0 && currentStepIndex < totalSteps - 1 && (
              <button
                className="onboarding-overlay__skip"
                onClick={handleSkip}
              >
                Skip
              </button>
            )}
            <Button
              variant="primary"
              size="md"
              onClick={handleAction}
              rightIcon={effectiveStep.action === 'finish' ? null : <Icon name="arrow-right" size={14} />}
            >
              {actionLabel}
            </Button>
          </div>

          {/* Dismiss link */}
          <button
            className="onboarding-overlay__dismiss"
            onClick={handleDismiss}
          >
            Skip tour
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default OnboardingOverlay
